// eslint-disable-next-line  import/no-unresolved
import { useState } from 'react';
import { AudioConfig, ResultReason, SpeechRecognizer } from 'microsoft-cognitiveservices-speech-sdk';

import { createSpeechConfig } from './speechConfig';

type TranscribeAudioParams = {
  audio: Blob;
  language: string;
};

export const useSpeechToText = () => {
  const [transcribing, setTranscribing] = useState(false);

  const transcribeAudio = async ({ audio, language }: TranscribeAudioParams): Promise<string | null> => {
    setTranscribing(true);

    try {
      const speechConfig = createSpeechConfig();
      speechConfig.speechRecognitionLanguage = language;

      const audioFile = new File([audio], 'recording.wav', { type: 'audio/wav' });
      const recognizer = new SpeechRecognizer(speechConfig, AudioConfig.fromWavFileInput(audioFile));

      return await new Promise<string | null>(resolve => {
        let transcription = '';
        let settled = false;
        let timeout = 0;

        const finish = (text: string) => {
          if (settled) {
            return;
          }

          settled = true;
          window.clearTimeout(timeout);
          recognizer.close();
          resolve(text.trim() || null);
        };

        const stopRecognition = () => {
          recognizer.stopContinuousRecognitionAsync(
            () => {
              finish(transcription);
            },
            error => {
              if (!settled) {
                settled = true;
                window.clearTimeout(timeout);
                recognizer.close();
                // eslint-disable-next-line no-console
                console.error('Failed to stop recognition cleanly.', error);
                resolve(transcription.trim() || null);
              }
            },
          );
        };

        timeout = window.setTimeout(() => {
          if (!settled) {
            stopRecognition();
          }
        }, 60000);

        recognizer.recognizing = (_sender, event) => {
          // eslint-disable-next-line no-console
          console.info(`Partial result: ${event.result.text}`);
        };

        recognizer.recognized = (_sender, event) => {
          if (event.result.reason === ResultReason.RecognizedSpeech) {
            try {
              const json = JSON.parse(event.result.json);
              const displayText = json?.DisplayText ?? json?.NBest?.[0]?.Display ?? event.result.text;

              // eslint-disable-next-line no-console
              console.info(`Final result: ${displayText}`);
              transcription += `${displayText} `;
            } catch (_error) {
              // eslint-disable-next-line no-console
              console.warn('Failed to parse result JSON, falling back to plain text.');
              transcription += `${event.result.text} `;
            }
          } else if (event.result.reason === ResultReason.NoMatch) {
            // eslint-disable-next-line no-console
            console.info('No speech recognized.');
          }
        };

        recognizer.canceled = (_sender, event) => {
          // eslint-disable-next-line no-console
          console.info(`Recognition canceled: ${event.errorDetails || 'no error details'}`);

          if (settled) {
            return;
          }

          stopRecognition();
        };

        recognizer.sessionStopped = () => {
          // eslint-disable-next-line no-console
          console.info('Session stopped.');

          if (!settled) {
            window.clearTimeout(timeout);
            stopRecognition();
          }
        };

        recognizer.startContinuousRecognitionAsync(undefined, error => {
          if (!settled) {
            settled = true;
            window.clearTimeout(timeout);
            recognizer.close();
            // eslint-disable-next-line no-console
            console.error('Failed to start transcription.', error);
            resolve(null);
          }
        });
      });
    } finally {
      setTranscribing(false);
    }
  };

  return { transcribeAudio, transcribing };
};
